//frontend/src/components/handover/HandoverNotification.tsx
import React, { useState, useEffect, useRef } from 'react';
import { 
  Badge, 
  ActionIcon, 
  Popover, 
  Avatar, 
  Text, 
  Box, 
  Divider, 
  Button,
  Loader,
  Stack,
  Group,
  Paper,
  Transition
} from '@mantine/core';
import { 
  IconBell,
  IconHeadphones,
  IconBrandWhatsapp,
  IconMessages,
  IconClock,
  IconUser, IconUserCircle, IconRobot, IconBrain, IconMessageChatbot,
  IconHeadset, IconUserBolt, IconUserCode, IconUserCog, IconUserHeart,
  IconUserShield, IconUserStar, IconAffiliate, IconAlien, IconBrandHipchat,
  IconMoodSmile, IconMoodHappy, IconMoodCrazyHappy, IconGhost, IconSkull,
  IconPaw, IconBalloon, IconCat, IconDog, IconFish, IconButterfly,
  IconCpu, IconBrandOpenai, IconBulb, IconSparkles
} from '@tabler/icons-react';
import type { Icon as TablerIcon } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { apiClient, AxiosError } from '../../services/authService';
import type { AxiosResponse } from '../../services/authService';
import { useTranslation } from 'react-i18next';
import { logger } from '@/utils/logger';

interface HandoverMetadata {
  handover_reason?: string;
  channel?: string;
  [key: string]: unknown;
}

interface Handover {
  session_id: string;
  updated_at: string;
  metadata?: HandoverMetadata;
  operator_id?: string | null;
  status?: string;
}

interface ChannelInfo {
  icon: React.ReactElement;
  color: string;
  type: 'whatsapp' | 'whatsappcloud' | 'web' | 'unknown';
}

interface AvatarIconData {
  icon: TablerIcon;
  name: string;
  category: 'bot' | 'user' | 'mood' | 'animals';
}

interface PendingHandoversApiResponse {
  success: boolean;
  data?: Handover[];
  error?: string;
}

interface UserApiResponse {
  success: boolean;
  data?: {
    id: string;
    can_handover: boolean;
    client_id: string | null;
    role: string;
  };
}

interface HandoverSSEEvent {
  handovers?: Handover[];
  sessionId?: string;
  newHandover?: boolean;
}

interface UserData {
  id?: string;
  client_id?: string | null;
  role?: string;
}

const AVATAR_ICONS: AvatarIconData[] = [
  { icon: IconRobot, name: 'Robot', category: 'bot' },
  { icon: IconBrain, name: 'Brain', category: 'bot' },
  { icon: IconMessageChatbot, name: 'Chatbot', category: 'bot' },
  { icon: IconCpu, name: 'CPU', category: 'bot' },
  { icon: IconBrandOpenai, name: 'AI', category: 'bot' },
  { icon: IconHeadset, name: 'Support', category: 'bot' },
  { icon: IconBulb, name: 'Idea', category: 'bot' },
  { icon: IconSparkles, name: 'Magic', category: 'bot' },
  { icon: IconUser, name: 'User', category: 'user' },
  { icon: IconUserCircle, name: 'User Circle', category: 'user' },
  { icon: IconUserBolt, name: 'Power User', category: 'user' },
  { icon: IconUserCode, name: 'Developer', category: 'user' },
  { icon: IconUserCog, name: 'Settings User', category: 'user' },
  { icon: IconUserHeart, name: 'Loved User', category: 'user' },
  { icon: IconUserShield, name: 'Protected User', category: 'user' },
  { icon: IconUserStar, name: 'Star User', category: 'user' },
  { icon: IconMoodSmile, name: 'Smile', category: 'mood' },
  { icon: IconMoodHappy, name: 'Happy', category: 'mood' },
  { icon: IconMoodCrazyHappy, name: 'Very Happy', category: 'mood' },
  { icon: IconGhost, name: 'Ghost', category: 'mood' },
  { icon: IconAlien, name: 'Alien', category: 'mood' },
  { icon: IconCat, name: 'Cat', category: 'animals' },
  { icon: IconDog, name: 'Dog', category: 'animals' },
  { icon: IconFish, name: 'Fish', category: 'animals' },
  { icon: IconButterfly, name: 'Butterfly', category: 'animals' },
  { icon: IconPaw, name: 'Paw', category: 'animals' }
];

const getChannelInfo = (sessionId: string | null | undefined, metadata?: HandoverMetadata): ChannelInfo => {
  if (!sessionId) {
    return {
      icon: <IconMessages size={16} />,
      color: '#757575',
      type: 'unknown'
    };
  }

  // Check metadata.channel first (for agent conversations)
  if (metadata?.channel) {
    const channel = metadata.channel.toLowerCase();
    if (channel === 'whatsapp_cloud' || channel === 'whatsapp-cloud') {
      return {
        icon: <IconBrandWhatsapp size={16} />,
        color: '#34B7F1',
        type: 'whatsappcloud'
      };
    } else if (channel === 'whatsapp') {
      return {
        icon: <IconBrandWhatsapp size={16} />,
        color: '#25D366',
        type: 'whatsapp'
      };
    }
  }

  // Fallback to sessionId prefix check (for langchain conversations)
  if (sessionId.startsWith('whatsapp-')) {
    return {
      icon: <IconBrandWhatsapp size={16} />,
      color: '#25D366',
      type: 'whatsapp'
    };
  } else if (sessionId.startsWith('whatsappcloud-')) {
    return {
      icon: <IconBrandWhatsapp size={16} />,
      color: '#34B7F1',
      type: 'whatsappcloud'
    };
  }

  return {
    icon: <IconMessages size={16} />,
    color: '#2196F3',
    type: 'web'
  };
};

const formatSessionId = (sessionId: string | null | undefined): string => {
  if (!sessionId) return '';

  const { type } = getChannelInfo(sessionId);

  if (type === 'whatsapp') {
    const parts = sessionId.split('-');
    if (parts.length >= 3) {
      const phoneNumber = parts.slice(2).join('-');
      const cleanNumber = phoneNumber.replace('@c.us', '');
      return `+${cleanNumber}`;
    }
  }

  return sessionId.slice(0, 12) + '...';
};

const renderAvatar = (sessionId: string, isUser = false): React.ReactElement => {
  const channelInfo = getChannelInfo(sessionId);
  
  return (
    <Avatar 
      color={channelInfo.color} 
      size="md"
      radius="xl"
      variant="light"
    >
      {channelInfo.icon}
    </Avatar>
  );
};

const getAvatarIcon = (iconName: string): TablerIcon => {
  const iconData = AVATAR_ICONS.find(i => i.name === iconName);
  return iconData ? iconData.icon : IconUser;
};

const HandoverNotification = (): React.ReactElement => {
  const { t } = useTranslation('handoverNotification');
  
  const [pendingHandovers, setPendingHandovers] = useState<Handover[]>([]);
  const [notificationCount, setNotificationCount] = useState<number>(0);
  const [opened, setOpened] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [handoverEnabled, setHandoverEnabled] = useState<boolean>(true);
  const [clientId, setClientId] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [operatorId, setOperatorId] = useState<string | null>(null);
  const [badgeAnimation, setBadgeAnimation] = useState<boolean>(false);
  const [audioEnabled, setAudioEnabled] = useState<boolean>(true);

  const eventSourceRef = useRef<EventSource | null>(null);
  const clientIdRef = useRef<string>((() => {
    const key = 'handoverNotificationTabId';
    let tabId = sessionStorage.getItem(key);
    if (!tabId) {
      tabId = Math.random().toString(36).substring(2, 9);
      sessionStorage.setItem(key, tabId);
    }
    return `handover-notification-${tabId}`;
  })());
  const connectedRef = useRef<boolean>(false);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const reconnectAttemptRef = useRef<number>(0);
  
  const navigate = useNavigate();

  const playNotificationSound = (): void => {
    if (!audioEnabled) return;
    
    try {
      if (!audioRef.current) {
        audioRef.current = new Audio('/sounds/handover-notification.wav');
      }
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch((err: unknown) => {
        if (err instanceof DOMException && err.name === 'NotAllowedError') {
          logger.log(t('console.autoplayNotAllowed'));
        } else {
          logger.warn('Audio play error:', err);
        }
      });
    } catch (err: unknown) {
      logger.log(t('console.audioNotSupported'));
    }
  };

  const fetchAudioPreference = async (): Promise<void> => {
    try {
      // 🔒 SECURITY: Token is sent automatically via httpOnly cookie (withCredentials: true)
      const response = await apiClient.get('/api/users/me/notification-preferences');

      if (response.data.success && response.data.data) {
        setAudioEnabled(response.data.data.notify_audio ?? true);
      }
    } catch (err) {
      logger.warn('Failed to fetch audio preference:', err);
    }
  };

  const loadPendingHandovers = async (): Promise<Handover[]> => {
    try {
      setLoading(true);
      setError(null);

      const response: AxiosResponse<PendingHandoversApiResponse> = await apiClient.get('/api/handover/pending');

      if (response.data.success) {
        const uniqueHandovers = response.data.data ? 
          Array.from(new Map(response.data.data.map(item => [item.session_id, item])).values()) : 
          [];

        setPendingHandovers(uniqueHandovers);
        setNotificationCount(uniqueHandovers.length); 

        return uniqueHandovers;
      } else {
        logger.error(t('console.errorLoadRequests'), response.data.error);
        setError(t('errors.cannotLoadNotifications'));
        return [];
      }
    } catch (err: unknown) {
      if (err instanceof AxiosError) {
        logger.error(t('console.errorAPI'), err.message);
      } else if (err instanceof Error) {
        logger.error(t('console.errorAPI'), err.message);
      } else {
        logger.error(t('console.errorAPI'), String(err));
      }
      setError(t('errors.connectionError'));
      return [];
    } finally {
      setLoading(false);
    }
  };

  const handleHandoverClick = (sessionId: string): void => {
    setOpened(false);
    navigate('/advanced/handover', { state: { selectedSession: sessionId } });
  };

  const setupSSE = (): (() => void) | void => {
    if (!handoverEnabled) {
      logger.log(t('console.handoverDisabled'));
      return;
    }

    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    const clientIdValue = clientIdRef.current;

    // 🔒 SECURITY: No longer read user data from localStorage
    // Auth token is sent via httpOnly cookie; user info comes from state
    const currentClientId = clientId || null;
    const currentRole = userRole || 'operator';

    // 🔒 SECURITY: token is automatically sent via httpOnly cookie by browser
    // EventSource automatically includes cookies for same-origin requests
    const url = `/api/langchain/sse/handover?clientId=${clientIdValue}&operatorId=${operatorId || ''}&role=${currentRole}${currentClientId ? `&client_id=${currentClientId}` : ''}&can_handover=true`;

    const eventSource = new EventSource(url, { withCredentials: true });

    eventSource.onopen = (): void => {
      logger.log(t('console.sseEstablished'));
      connectedRef.current = true;
      reconnectAttemptRef.current = 0; // Reset on successful connection
      logger.log(t('console.sseConnectedTrue'));

      loadPendingHandovers().then(handovers => {
        logger.log('Loaded handovers on SSE open:', handovers.length);
      }).catch((err: unknown) => {
        logger.error('Error loading handovers on SSE open:', err);
      });
    };

    eventSource.onmessage = (event: MessageEvent): void => {
      logger.log('SSE message received:', event.data);
    };

    eventSource.onerror = (error: Event): void => {
      logger.warn(t('console.sseError'), error);
      connectedRef.current = false;
      logger.log(t('console.sseConnectedFalse'));

      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }

      // BUG FIX: Use exponential backoff with max retries to prevent infinite 401 loops
      const attempt = (reconnectAttemptRef.current || 0);
      const maxRetries = 10;
      if (attempt >= maxRetries) {
        logger.error(`[SSE] Max reconnect attempts (${maxRetries}) reached, stopping`);
        return;
      }
      const delay = Math.min(5000 * Math.pow(2, attempt), 60000);
      reconnectAttemptRef.current = attempt + 1;

      setTimeout(() => {
        if (eventSourceRef.current === null) {
          setupSSE();
        }
      }, delay);
    };

    eventSource.addEventListener('pending-handovers', (e: MessageEvent): void => {
      try {
        const data = JSON.parse(e.data) as HandoverSSEEvent;
        logger.log(t('console.pendingReceived'), data);

        const uniqueHandovers = data.handovers ?
          Array.from(new Map(data.handovers.map(item => [item.session_id, item])).values()) :
          [];

        setPendingHandovers(prev => {
          const prevCount = prev.length;
          const newCount = uniqueHandovers.length;

          if (newCount > prevCount) {
            playNotificationSound();
            setBadgeAnimation(true);
            setTimeout(() => setBadgeAnimation(false), 1000);
          }

          return uniqueHandovers;
        });

        setNotificationCount(uniqueHandovers.length);
      } catch (err: unknown) {
        if (err instanceof Error) {
          logger.error('Error parsing pending-handovers event:', err.message);
        } else {
          logger.error('Error parsing pending-handovers event:', String(err));
        }
      }
    });

    eventSource.addEventListener('handover-requested', (e: MessageEvent): void => {
      loadPendingHandovers().catch((err: unknown) => {
        logger.error('Error loading handovers on handover-requested:', err);
      });
    });

    eventSource.addEventListener('handover-assigned', (e: MessageEvent): void => {
      logger.log(t('console.handoverAssigned'));
      loadPendingHandovers().catch((err: unknown) => {
        logger.error('Error loading handovers on handover-assigned:', err);
      });
    });

    eventSource.addEventListener('handover-completed', (e: MessageEvent): void => {
      try {
        const data = JSON.parse(e.data) as HandoverSSEEvent;
        logger.log(t('console.handoverCompleted'), data);

        setTimeout(() => {
          loadPendingHandovers().then(() => {
            logger.log(t('console.listUpdated').replace('${count}', String(pendingHandovers.length)));
          }).catch((err: unknown) => {
            logger.error('Error loading handovers after handover-completed:', err);
          });
        }, 100);

        if (data && data.sessionId) {
          setPendingHandovers(prev => {
            const filtered = prev.filter(h => h.session_id !== data.sessionId);
            setNotificationCount(filtered.length);
            return filtered;
          });
        }
      } catch (err: unknown) {
        if (err instanceof Error) {
          logger.error(t('console.errorHandoverCompleted'), err.message);
        } else {
          logger.error(t('console.errorHandoverCompleted'), String(err));
        }
        loadPendingHandovers().catch((e: unknown) => {
          logger.error('Error reloading handovers:', e);
        });
      }
    });

    eventSource.addEventListener('client-message', (e: MessageEvent): void => {
      try {
        const data = JSON.parse(e.data);
        logger.log('[HandoverNotification] Received client-message:', data);

        playNotificationSound();

        window.dispatchEvent(new CustomEvent('handover-client-message', { detail: data }));
      } catch (err: unknown) {
        logger.error('Error parsing client-message event:', err);
      }
    });

    eventSource.addEventListener('pending-handovers-update', (e: MessageEvent): void => {
      try {
        const data = JSON.parse(e.data) as HandoverSSEEvent;
        logger.log(t('console.pendingUpdate'), data);

        Promise.resolve().then(async () => {
          await loadPendingHandovers();

          if (data.newHandover) {
            playNotificationSound();
            setBadgeAnimation(true);
            setTimeout(() => setBadgeAnimation(false), 1000);
          }

          logger.log(t('console.stateUpdated').replace('${count}', String(notificationCount)));
        }).catch((err: unknown) => {
          logger.error('Error in pending-handovers-update handler:', err);
        });
      } catch (err: unknown) {
        if (err instanceof Error) {
          logger.error(t('console.errorPendingUpdate'), err.message);
        } else {
          logger.error(t('console.errorPendingUpdate'), String(err));
        }
      }
    });

    eventSourceRef.current = eventSource;
    
    return (): void => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  };

  const checkUserHandoverPermission = async (): Promise<boolean> => {
    try {
      // 🔒 SECURITY: Use /api/users/me endpoint with httpOnly cookie auth
      // instead of reading userId from localStorage
      const response: AxiosResponse<UserApiResponse> = await apiClient.get('/api/users/me');
      if (response.data.success && response.data.data) {
        const canHandover = response.data.data.can_handover;
        const userClientId = response.data.data.client_id;
        const role = response.data.data.role;
        const userId = response.data.data.id;

        setHandoverEnabled(canHandover);
        setClientId(userClientId);
        setUserRole(role);
        setOperatorId(userId);

        if (!canHandover && eventSourceRef.current) {
          logger.log(t('console.closingSSE'));
          eventSourceRef.current.close();
          eventSourceRef.current = null;
          setPendingHandovers([]);
          setNotificationCount(0);
        }

        return canHandover;
      }
      return true;
    } catch (err: unknown) {
      if (err instanceof AxiosError) {
        logger.error(t('console.errorUserInfo'), err.message);
      } else if (err instanceof Error) {
        logger.error(t('console.errorUserInfo'), err.message);
      } else {
        logger.error(t('console.errorUserInfo'), String(err));
      }
      return true;
    }
  };

  const setupPollingFallback = (): void => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    // FIX: Polling only when SSE is disconnected, with longer interval (60s)
    // When SSE is connected, real-time events handle updates
    pollingIntervalRef.current = setInterval(() => {
      if (!connectedRef.current) {
        loadPendingHandovers().catch((err: unknown) => {
          logger.error('Error in polling fallback:', err);
        });
      }
    }, 60000); // 60s when disconnected - SSE handles real-time updates when connected
  };

  useEffect(() => {
    const init = async (): Promise<void> => {
      const isEnabled = await checkUserHandoverPermission();
      if (isEnabled) {
        await fetchAudioPreference();
        await loadPendingHandovers();
        setupSSE();
        setupPollingFallback();
      }
    };

    init().catch((err: unknown) => {
      logger.error('Error initializing HandoverNotification:', err);
    });

    return (): void => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }

      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (notificationCount > 0) {
      setBadgeAnimation(true);
      const timer = setTimeout(() => setBadgeAnimation(false), 600);
      return (): void => clearTimeout(timer);
    }
  }, [notificationCount]);

  return (
    <>
      <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }

        @keyframes bounce {
          0%, 100% { transform: scale(1); }
          25% { transform: scale(0.9); }
          50% { transform: scale(1.15); }
          75% { transform: scale(0.95); }
        }
      `}</style>

      <Popover
        width={350}
        position="bottom-end"
        withArrow
        shadow="lg"
        opened={opened}
        onChange={setOpened}
      >
        <Popover.Target>
          <ActionIcon 
            color="gray" 
            variant="subtle"
            radius="md"
            size="lg"
            onClick={() => setOpened((o) => !o)}
            style={{ position: 'relative' }}
          >
            <Transition
              mounted={notificationCount > 0}
              transition="pop"
              duration={400}
            >
              {(styles) => (
                <Badge 
                  color="red" 
                  size="xs"
                  variant="filled"
                  style={{
                    ...styles,
                    position: 'absolute',
                    top: -5,
                    right: -5,
                    minWidth: 20,
                    height: 20,
                    padding: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    animation: badgeAnimation ? 'bounce 0.6s ease' : 'pulse 2s infinite'
                  }}
                >
                  {notificationCount > 99 ? '99+' : notificationCount}
                </Badge>
              )}
            </Transition>
            <IconBell 
              size={20} 
              color={notificationCount > 0 ? '#2F3289' : 'currentColor'} 
            />
          </ActionIcon>
        </Popover.Target>

        <Popover.Dropdown p={0}>
          <Paper radius="md" style={{ overflow: 'hidden' }}>
            <Box 
              p="md" 
              style={{ 
                borderBottom: '1px solid #e5e7eb',
                background: 'linear-gradient(135deg, rgba(47, 50, 137, 0.05), rgba(64, 38, 106, 0.05))'
              }}
            >
              <Stack gap="xs">
                <Group justify="space-between" align="center">
                  <Text fw={600} size="lg" style={{ color: '#2F3289' }}>
                    {t('ui.header')}
                  </Text>
                  {notificationCount > 0 && (
                    <Badge 
                      color="red" 
                      size="sm" 
                      variant="gradient"
                      gradient={{ from: 'red', to: 'pink', deg: 45 }}
                    >
                      {notificationCount}
                    </Badge>
                  )}
                </Group>
                <Group gap="xs">
                  <Text size="sm" c="dimmed">
                    {notificationCount > 0 
                      ? t('ui.requestsWaiting', { count: notificationCount })
                      : t('ui.noRequestsWaiting')}
                  </Text>
                </Group>
              </Stack>
            </Box>

            {loading ? (
              <Box p="md" style={{ textAlign: 'center' }}>
                <Loader size="sm" />
              </Box>
            ) : error ? (
              <Box p="md" style={{ textAlign: 'center' }}>
                <Text size="sm" c="red">{error}</Text>
                <Button
                  size="xs"
                  variant="light"
                  color="red"
                  onClick={() => {
                    loadPendingHandovers().catch((err: unknown) => {
                      logger.error('Error retrying load:', err);
                    });
                  }}
                  mt="xs"
                  radius="md"
                >
                  {t('errors.retry')}
                </Button>
              </Box>
            ) : (
              <Box style={{ maxHeight: 350, overflow: 'auto' }}>
                {pendingHandovers.length === 0 ? (
                  <Box p="xl" style={{ textAlign: 'center' }}>
                    <Stack gap="md" align="center">
                      <IconHeadphones size={48} style={{ color: '#868e96' }} />
                      <Text size="sm" c="dimmed">
                        {t('ui.noRequestsMessage')}
                      </Text>
                    </Stack>
                  </Box>
                ) : (
                  <Box>
                    {pendingHandovers.map((handover, index) => {
                      const channelInfo = getChannelInfo(handover.session_id, handover.metadata);
                      const timeSinceUpdate = new Date().getTime() - new Date(handover.updated_at).getTime();
                      const minutes = Math.floor(timeSinceUpdate / (1000 * 60));
                      const timeAgo = minutes < 1 ? t('timeAgo.now') : 
                                    minutes < 60 ? t('timeAgo.minutesAgo', { count: minutes }) : 
                                    t('timeAgo.hoursAgo', { count: Math.floor(minutes / 60) });

                      return (
                        <div key={handover.session_id}>
                          {index > 0 && <Divider />}
                          <Box
                            p="md"
                            style={{
                              cursor: 'pointer',
                              borderLeft: '4px solid transparent',
                              transition: 'all 0.2s ease'
                            }}
                            sx={{
                              '&:hover': {
                                backgroundColor: 'rgba(47, 50, 137, 0.03)',
                                borderLeft: '4px solid #2F3289'
                              }
                            }}
                            onClick={() => handleHandoverClick(handover.session_id)}
                          >
                            <Group gap="sm">
                              {renderAvatar(handover.session_id)}
                              <Box style={{ flex: 1, minWidth: 0 }}>
                                <Text size="sm" fw={600}>
                                  {formatSessionId(handover.session_id)}
                                </Text>
                                <Text size="xs" c="dimmed" truncate>
                                  {handover.metadata?.handover_reason || t('ui.defaultReason')}
                                </Text>
                                <Group gap="xs" mt={4}>
                                  <IconClock size={12} style={{ color: '#868e96' }} />
                                  <Text size="xs" c="dimmed">
                                    {timeAgo}
                                  </Text>
                                </Group>
                              </Box>
                            </Group>
                          </Box>
                        </div>
                      );
                    })}
                  </Box>
                )}
              </Box>
            )}

            <Box 
              p="md" 
              style={{ 
                borderTop: '1px solid #e5e7eb', 
                textAlign: 'center',
                backgroundColor: '#fafbfc'
              }}
            >
              <Button 
                variant="gradient"
                gradient={{ from: '#2F3289', to: '#40266A', deg: 45 }}
                size="sm" 
                leftSection={<IconHeadphones size={16} />}
                onClick={() => {
                  setOpened(false);
                  navigate('/advanced/handover');
                }}
                radius="md"
                style={{
                  fontWeight: 500,
                  transition: 'transform 0.2s ease'
                }}
                sx={{
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(47, 50, 137, 0.25)'
                  }
                }}
              >
                {t('ui.goToDashboard')}
              </Button>
            </Box>
          </Paper>
        </Popover.Dropdown>
      </Popover>
    </>
  );
};

export default HandoverNotification;
